import React, { useState, useRef } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import makeStyles from '@material-ui/core/styles/makeStyles';
import brandColors from 'styles/brandColors';
import { transparentize } from 'polished';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _differenceBy from 'lodash/differenceBy';
import { Prompt, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useData, useStatus } from 'resift';
import getRoles from 'fetches/rolesAndPermissions/getRoles';
import makeGetRole from 'fetches/rolesAndPermissions/makeGetRole';
import makeUpdateRole, { UpdateRoleRequest } from 'fetches/rolesAndPermissions/makeUpdateRole';
import makeDeletePermissionFromRole from 'fetches/rolesAndPermissions/makeDeletePermissionFromRole';
import makeDeleteRole from 'fetches/rolesAndPermissions/makeDeleteRole';
import makeCreatePermissionForRole from 'fetches/rolesAndPermissions/makeCreatePermissionForRole';
import showSnackbar from 'helpers/showSnackbar';
import { HOME_URL, ROLES_AND_PERMISSIONS_URL } from 'components/Router/consts';
import { TOTAL_NUM_OF_AVAILABLE_PERMISSIONS } from 'pages/RolesAndPermissions/consts';
import * as Types from 'pages/AttributesEdit/typings';
import * as RoleTypes from 'pages/RolesAndPermissions/typings';
import { Theme } from 'styles/ThemeTyping';

import { Formik, Form } from 'formik';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import CancelButton from 'components/Page/CancelButton';
import RoleNameAndDescription from 'pages/RolesAndPermissions/molecules/RoleNameAndDescription';
import ChipRow from 'pages/RolesAndPermissions/molecules/ChipRow';
import AddPermissionsDropdown from 'pages/RolesAndPermissions/RoleConfiguration/AddPermissionsDropdown';
import EditDisabledNotice from 'pages/RolesAndPermissions/molecules/EditDisabledNotice';
import DeleteConfirmationDialog from 'molecules/DeleteConfirmationDialog';

interface Props {
  role: RoleTypes.Role;
  onClose: () => void;
  fromEditUserId?: string;
}

function RoleManageForm({ role, onClose, fromEditUserId }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const history = useHistory();
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [deleteDialogInput, setDeleteDialogInput] = useState('');
  const [deletePermissionDialogOpen, setDeletePermissionDialogOpen] = useState(false);
  const [deletePermissionDialogInput, setDeletePermissionDialogInput] = useState('');
  const [permissionIdToDelete, setPermissionIdToDelete] = useState<string>('');

  const deleteRole = makeDeleteRole(role.id);
  const deletePermissionFromRole = makeDeletePermissionFromRole(role.id);
  const deletePermissionFromRoleStatus = useStatus(deletePermissionFromRole);
  const deleteRoleStatus = useStatus(deleteRole);
  const rolesData = useData(getRoles);
  const updateRole = makeUpdateRole(role.id);
  const getRole = makeGetRole(role.id);
  const createPermissionForRole = makeCreatePermissionForRole(role.id);
  const newPermissionRef = useRef<HTMLDivElement>(null);
  const [showNewPermissionInput, setShowNewPermissionInput] = useState(false);
  const [permissionDropdownAnchorEl, setPermissionDropdownAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [addPermissionDropdownOpen, setAddPermissionDropdownOpen] = useState(false);
  const roleNames = rolesData ? rolesData.results.map((role: RoleTypes.Role) => role.name) : [];

  const membersEditable = _get(role, 'membersEditable', false);
  const permissionEditable = _get(role, 'permissionsEditable', false);
  const memberCount = _get(role, '_memberCount', 0);
  const permissions = _get(role, '_permissions', []) as Array<RoleTypes.PermissionResponse>;
  const isDefaultRole = _get(role, 'isDefault', false);
  const disabledPermissionsEdit = !permissionEditable;
  const hasAddedAllPermissions = permissions.length === TOTAL_NUM_OF_AVAILABLE_PERMISSIONS;
  const disableAddPermission = hasAddedAllPermissions || disabledPermissionsEdit;
  const addMembersButtonText = membersEditable ? 'Add Members' : 'View Members';
  const nameOfPermissionToDelete = _get(
    permissions.find(p => _get(p, ['permission', 'id']) === permissionIdToDelete),
    'permission.name',
    '',
  );

  showSnackbar({
    status: deleteRoleStatus,
    message: 'Successfully deleted role.',
    variant: 'success',
    errorMessage: 'An error occurred while deleting the role.',
  });

  showSnackbar({
    status: deletePermissionFromRoleStatus,
    message: 'Successfully removed permission from role.',
    variant: 'success',
    errorMessage: 'An error occurred while removing the permission from role.',
  });

  const handleDeleteRoleDialogOpen = () => {
    setDeleteRoleDialogOpen(true);
  };

  const handleDeleteDialogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteDialogInput(e.target.value);
  };

  const handleDeleteRole = async () => {
    await dispatch(deleteRole());
    analytics.track('Roles/Permissions - Create Role - Delete Role Clicked', {
      category: 'AD - Roles/Permissions',
    });

    history.replace(`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}`);
  };

  const handleDeletePermissionDialogOpen = (permissionId: string) => {
    setPermissionIdToDelete(permissionId);
    setDeletePermissionDialogOpen(true);
  };

  const handleDeletePermissionDialogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeletePermissionDialogInput(e.target.value);
  };

  const handleDeletePermission = async () => {
    await dispatch(deletePermissionFromRole(permissionIdToDelete));
    dispatch(getRole());
  };

  const handleAddPermission = () => {
    analytics.track('Roles/Permissions - Create Role - Add Permission', {
      category: 'AD - Roles/Permissions',
    });

    setAddPermissionDropdownOpen(true);
    setShowNewPermissionInput(true);

    setTimeout(() => {
      if (newPermissionRef.current) {
        setPermissionDropdownAnchorEl(newPermissionRef.current);
      }
    }, 0);
  };

  const handleSelectPermission = async (permissionId: string) => {
    await dispatch(createPermissionForRole({ permissionId }));

    history.push(
      `${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/roles/${role.id}/permissions/${permissionId}`,
      { fromEditUserId },
    );
  };

  const handleEditPermission = (permissionId: string) => {
    analytics.track('Roles/Permissions - Create Role - Manage Permissions Clicked', {
      category: 'AD - Roles/Permissions',
    });

    history.push(
      `${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/roles/${role.id}/permissions/${permissionId}`,
      { fromEditUserId },
    );
  };

  const handleEditRoleMembers = () => {
    analytics.track('Roles/Permissions - Create Role - Manage Members Clicked', {
      category: 'AD - Roles/Permissions',
    });

    history.push(`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/roles/${role.id}/manage-members`, {
      fromEditUserId,
    });
  };

  const initialState = {
    name: _get(role, 'name', ''),
    description: _get(role, 'description', ''),
    createdAt: _get(role, 'createdAt', ''),
    updatedAt: _get(role, 'updatedAt', ''),
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;

    if (!values.name.length) {
      errors.name = 'Name is required';
    }

    if (!values.description.length) {
      errors.description = 'Description is required';
    }

    if (roleNames.includes(values.name) && values.name !== role.name) {
      errors.name = 'Role name already exists, please choose a different name';
    }

    return errors;
  };

  return (
    <>
      {role.isDefault && (
        <EditDisabledNotice text="This is a default role and some information may not be editable." />
      )}

      <Formik initialValues={initialState} onSubmit={() => {}} validate={handleValidation}>
        {props => {
          const { dirty, values, setFieldValue, resetForm } = props;
          const errors = props.errors as Types.AttributesFormErrors;
          const roleName = _get(values, 'name', '');
          const roleDescription = _get(values, 'description', '');

          const handleSave = async () => {
            const roleUpdateRequest = {} as UpdateRoleRequest;
            if (roleName !== role.name) {
              roleUpdateRequest['name'] = roleName;
            }
            if (roleDescription !== role.description) {
              roleUpdateRequest['description'] = roleDescription;
            }

            await dispatch(updateRole(roleUpdateRequest));
            resetForm();
            dispatch(getRole());
          };

          return (
            <Form className={classes.root}>
              <>
                <RoleNameAndDescription
                  nameValue={roleName}
                  descriptionValue={roleDescription}
                  setFieldValue={setFieldValue}
                  isDefaultRole={isDefaultRole}
                  errors={errors}
                />

                <Card className={classes.card}>
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <Typography variant="h5">Role Members</Typography>

                      <Button color="secondary" onClick={handleEditRoleMembers}>
                        {addMembersButtonText}
                      </Button>
                    </div>
                    <Typography variant="body2" className={classes.subtitle}>
                      Manage the members of this role either by explicitly assigning them or
                      defining a dynamic filter.
                    </Typography>

                    {memberCount === 0 ? (
                      <Typography variant="body2" className={classes.subtitle}>
                        No members currently added. Select “Add Members” to get started.
                      </Typography>
                    ) : (
                      <ChipRow
                        label={`${role._memberCount} members`}
                        memberCount={role._memberCount}
                        disabledMembersEdit={!role.membersEditable}
                        onEdit={handleEditRoleMembers}
                        className={classes.manageRoleFormChip}
                      />
                    )}
                  </CardContent>
                </Card>

                <Card className={classes.card}>
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <Typography variant="h5">Permissions</Typography>
                      <Tooltip
                        title={
                          hasAddedAllPermissions
                            ? "You've already added all available permissions to this role."
                            : ''
                        }
                        placement="top"
                        arrow
                      >
                        <span>
                          <Button
                            color="secondary"
                            disabled={disableAddPermission}
                            onClick={handleAddPermission}
                          >
                            Add Permission
                          </Button>
                        </span>
                      </Tooltip>
                    </div>

                    <Typography variant="body2" className={classes.subtitle}>
                      Assign and manage permissions for this role. Learn more about the different
                      permission types{' '}
                      <Link
                        href="https://help.justsift.com/en/articles/12526057-sift-permission-types"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="secondary"
                      >
                        here
                      </Link>
                      .
                    </Typography>

                    {permissions.length === 0 ? (
                      <Typography variant="body2" className={classes.subtitle}>
                        No permissions currently added. Select “Add Permission” to get started.
                      </Typography>
                    ) : (
                      <div>
                        {permissions.map((permission: RoleTypes.PermissionResponse) => (
                          <ChipRow
                            key={permission.permission.name}
                            label={permission.permission.name}
                            onEdit={() => handleEditPermission(permission.permission.id)}
                            onDelete={
                              !disabledPermissionsEdit
                                ? () => handleDeletePermissionDialogOpen(permission.permission.id)
                                : undefined
                            }
                            disabledPermissionsEdit={disabledPermissionsEdit}
                            className={classes.manageRoleFormChip}
                            truncationDisabled
                          />
                        ))}
                      </div>
                    )}
                    {showNewPermissionInput && (
                      <div ref={newPermissionRef}>
                        <ChipRow
                          label={'Select a permission from below'}
                          className={classes.manageRoleFormChip}
                        />
                      </div>
                    )}
                    <AddPermissionsDropdown
                      onClick={handleSelectPermission}
                      anchorEl={permissionDropdownAnchorEl}
                      open={addPermissionDropdownOpen}
                      selectedPermissions={permissions}
                    />
                  </CardContent>
                </Card>

                <Card className={classes.card}>
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <Typography variant="h5">Delete Role</Typography>
                    </div>

                    <Typography variant="body2" className={classes.subtitle}>
                      Delete this role. All assigned members will lose the permissions associated
                      with this role.
                    </Typography>

                    <Tooltip
                      title={isDefaultRole ? 'Default roles cannot be deleted.' : ''}
                      placement="top"
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          className={classes.deleteRoleButton}
                          onClick={handleDeleteRoleDialogOpen}
                          disabled={isDefaultRole}
                        >
                          Delete Role
                        </Button>
                      </span>
                    </Tooltip>
                  </CardContent>
                </Card>

                <PageFormToolbar
                  rightSide
                  dirty={dirty || !_isEmpty(errors)}
                  preContent={<PageFormErrorMessage errors={errors} />}
                >
                  <CancelButton onClick={resetForm} />

                  <Button
                    onClick={handleSave}
                    disabled={!dirty || !_isEmpty(errors)}
                    variant="contained"
                    color="secondary"
                  >
                    Save
                  </Button>
                </PageFormToolbar>
                <Prompt
                  when={dirty}
                  message={
                    'Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?'
                  }
                />
              </>
              <DeleteConfirmationDialog
                dialogTitle="Are you sure you want to delete this role?"
                open={deleteRoleDialogOpen}
                onClose={() => setDeleteRoleDialogOpen(false)}
                onSave={handleDeleteRole}
                saveButtonText="Delete"
                disableSave={deleteDialogInput !== roleName}
              >
                <>
                  <Typography>
                    Deleting this role will cause any assigned members to lose this role’s
                    permissions. Ensure that you’ve assigned any permissions these users should keep
                    via other roles before continuing. <strong>You cannot undo this action.</strong>
                  </Typography>

                  <Typography className={classes.deletionWarning}>
                    To confirm, type “{roleName}” in the input below.
                  </Typography>
                </>

                <TextField
                  label={`Type ${roleName}`}
                  onChange={handleDeleteDialogChange}
                  className={classes.textField}
                  data-cy="role-deletion-input"
                  inputProps={{ 'aria-label': 'delete-role' }}
                />
              </DeleteConfirmationDialog>
              <DeleteConfirmationDialog
                dialogTitle="Confirmation"
                open={deletePermissionDialogOpen}
                onClose={() => setDeletePermissionDialogOpen(false)}
                onSave={handleDeletePermission}
                saveButtonText="Remove Permission"
                disableSave={deletePermissionDialogInput !== nameOfPermissionToDelete}
              >
                <>
                  <Typography>
                    Are you sure you want to remove this permission from this role?{' '}
                    <strong>You cannot undo this action.</strong>
                  </Typography>

                  <Typography className={classes.deletionWarning}>
                    To confirm, type “{nameOfPermissionToDelete}” in the input below.
                  </Typography>
                </>

                <TextField
                  label={`Type ${nameOfPermissionToDelete}`}
                  onChange={handleDeletePermissionDialogChange}
                  className={classes.textField}
                  data-cy="permission-deletion-input"
                  inputProps={{ 'aria-label': 'delete-permission' }}
                />
              </DeleteConfirmationDialog>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexDirection: 'column',
    width: '100%',
  },
  card: {
    flexGrow: 1,
    maxWidth: 900,
    marginRight: theme.spacing(3),
    width: '100%',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  subtitle: {
    color: theme.colors.gray2,
    marginBottom: theme.spacing(2),
  },
  deleteRoleButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
    marginTop: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  deletionWarning: {
    marginTop: theme.spacing(2),
  },
  textField: {
    width: '100%',
  },
  manageRoleFormChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    backgroundColor: transparentize(0.96, theme.palette.common.black),
    color: brandColors.grayScale.gray1,
    height: 28,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
    marginRight: theme.spacing(0.5),
  },
}));

export default RoleManageForm;
